import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/admin/layout/Layout';
import Table from '../components/shared/Table';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import Badge from '../components/shared/Badge';
import { adminApi } from '../api/admin';
import type { Device, DeviceType, Project } from '../types';
import type { ApiError } from '../api/client';

const POLL_ONLINE_MS = 5000;

const Devices: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [connectedDeviceIds, setConnectedDeviceIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<DeviceType>('VR');
  const [formProjectId, setFormProjectId] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchOnlineIds = useCallback(async () => {
    try {
      const { deviceIds } = await adminApi.devices.getOnlineIds();
      setConnectedDeviceIds(new Set(Array.isArray(deviceIds) ? deviceIds : []));
    } catch {
      setConnectedDeviceIds(new Set());
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.devices.list();
      setDevices(data);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await adminApi.projects.list();
      setProjects(data);
      if (data.length > 0 && !formProjectId) setFormProjectId(data[0].id);
    } catch {
      // ignore
    }
  }, [formProjectId]);

  useEffect(() => {
    document.title = 'Devices';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchOnlineIds();
    const interval = setInterval(fetchOnlineIds, POLL_ONLINE_MS);
    return () => clearInterval(interval);
  }, [fetchOnlineIds]);

  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const filteredDevices = devices.filter((d) => {
    const matchesSearch =
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !projectFilter || d.projectId === projectFilter;
    return matchesSearch && matchesProject;
  });

  /** Live status from WebSocket relay overrides DB status. */
  const getEffectiveStatus = (deviceId: string) =>
    connectedDeviceIds.has(deviceId) ? 'online' : 'offline';

  const onlineCount = devices.filter((d) => getEffectiveStatus(d.id) === 'online').length;
  const offlineCount = devices.filter((d) => getEffectiveStatus(d.id) === 'offline').length;

  const getStatusVariant = (status: string) =>
    status === 'online' ? 'success' : 'danger';

  const openCreate = () => {
    setEditingDevice(null);
    setFormName('');
    setFormType('VR');
    setFormProjectId(projects[0]?.id || '');
    setIsModalOpen(true);
  };

  const openEdit = (d: Device) => {
    setEditingDevice(d);
    setFormName(d.name);
    setFormType(d.type);
    setFormProjectId(d.projectId);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim() || !formProjectId) return;
    setSubmitLoading(true);
    setError('');
    try {
      if (editingDevice) {
        await adminApi.devices.update(editingDevice.id, {
          name: formName.trim(),
          type: formType,
          projectId: formProjectId,
        });
      } else {
        await adminApi.devices.create({
          name: formName.trim(),
          type: formType,
          projectId: formProjectId,
        });
      }
      setIsModalOpen(false);
      fetchDevices();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (d: Device) => {
    if (!window.confirm(`Remove device "${d.name}"?`)) return;
    setError('');
    try {
      await adminApi.devices.delete(d.id);
      fetchDevices();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {},
      () => {}
    );
  };

  const columns = [
    {
      header: 'Device Name',
      accessor: (row: Device) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500">{row.type}</div>
        </div>
      ),
    },
    {
      header: 'Device ID',
      accessor: (row: Device) => (
        <div className="flex items-center gap-2">
          <code className="device-id-code text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded truncate max-w-[280px]" title={row.id}>
            {row.id}
          </code>
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium whitespace-nowrap table-action-btn"
            onClick={() => copyToClipboard(row.id)}
          >
            Copy
          </button>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: (row: Device) => {
        const status = getEffectiveStatus(row.id);
        return (
          <Badge variant={getStatusVariant(status)}>{status}</Badge>
        );
      },
    },
    {
      header: 'Project',
      accessor: (row: Device) => (
        <span className="text-gray-900">{projectMap[row.projectId] || row.projectId}</span>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: Device) => (
        <div className="flex space-x-2">
          <button
            type="button"
            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="text-red-600 hover:text-red-900 text-sm font-medium"
            onClick={() => handleDelete(row)}
          >
            Remove
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout pageTitle="Devices">
      <div className="space-y-6">
        {error && (
          <div className="error-alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Online Devices</p>
                <p className="mt-2 text-3xl font-semibold text-green-600">{onlineCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Offline Devices</p>
                <p className="mt-2 text-3xl font-semibold text-red-600">{offlineCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-toolbar-primary">
            <Input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-toolbar-actions">
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              onClick={() => {
                fetchOnlineIds();
                fetchDevices();
              }}
            >
              Refresh
            </Button>
            <Button onClick={openCreate}>Register Device</Button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table columns={columns} data={filteredDevices} />
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={editingDevice ? 'Edit Device' : 'Register New Device'}
          footer={
            <>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitLoading || !formName.trim() || !formProjectId}
              >
                {submitLoading ? 'Saving...' : editingDevice ? 'Update' : 'Register Device'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {editingDevice && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device ID (for Unreal / Control Panel)</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-gray-600 bg-gray-100 px-3 py-2 rounded truncate">
                    {editingDevice.id}
                  </code>
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                    onClick={() => copyToClipboard(editingDevice.id)}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            <Input
              label="Device Name"
              placeholder="VR Headset #001"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device Type
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={formType}
                onChange={(e) => setFormType(e.target.value as DeviceType)}
              >
                <option value="VR">VR</option>
                <option value="Controller">Controller</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign to Project
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={formProjectId}
                onChange={(e) => setFormProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default Devices;
